import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateMeasurementEquipmentColumnData } from '../../store/actions/measurement-equipments.actions';
import { MeasuringEquipmentTableRow, MeasuringEquipmentsControlName } from '../../types/measuring-equipments';
import moment, { HTML5_FMT } from 'moment';
import { FormsConstants } from '../../types/forms-constants';
import { filter } from 'rxjs';
import { getNoMeasurementToolsUsed } from '../../store/selectors/form.selector';
import { ConfigConstants, isDefined } from '@evorait/pace-core';

@Component({
    selector: 'pace-forms-measuring-equipments-input-row',
    templateUrl: './measuring-equipments-input-row.component.html',
    styleUrl: './measuring-equipments-input-row.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuringEquipmentsInputRowComponent implements OnInit {
    @Input() public row: MeasuringEquipmentTableRow;
    @Input() public disabled = false;
    @Input() public noMeasurementToolsUsed: boolean;

    public measuringEquipmentForm: FormGroup;
    public descriptionControl: FormControl;
    public equipmentIdControl: FormControl;
    public calibratedControl: FormControl;
    public dateUsedControl: FormControl;

    private formBuilder = inject(FormBuilder);
    private store = inject(Store);
    private destroy = inject(DestroyRef);
    public constructor() {
        this.descriptionControl = this.formBuilder.control('');
        this.equipmentIdControl = this.formBuilder.control('');
        this.calibratedControl = this.formBuilder.control('', [this.calibratedDateValidator()]);
        this.dateUsedControl = this.formBuilder.control('', [this.dateUsedValidator()]);

        this.measuringEquipmentForm = this.formBuilder.group({
            [MeasuringEquipmentsControlName.Description]: this.descriptionControl,
            [MeasuringEquipmentsControlName.EquipmentId]: this.equipmentIdControl,
            [MeasuringEquipmentsControlName.Calibrated]: this.calibratedControl,
            [MeasuringEquipmentsControlName.DateUsed]: this.dateUsedControl,
        });
    }

    public ngOnInit(): void {
        this.store
            .select(getNoMeasurementToolsUsed)
            .pipe(filter(isDefined), takeUntilDestroyed(this.destroy))
            .subscribe((noToolsUsed) => {
                this.updateMeasuringEquipmentsValue(noToolsUsed);
            });

        const descriptionColumn = this.row.columns.find(
            (column) => column.columnName === MeasuringEquipmentsControlName.Description,
        );
        const equipmentIdColumn = this.row.columns.find(
            (column) => column.columnName === MeasuringEquipmentsControlName.EquipmentId,
        );
        const calibratedColumn = this.row.columns.find(
            (column) => column.columnName === MeasuringEquipmentsControlName.Calibrated,
        );
        const dateUsedColumn = this.row.columns.find(
            (column) => column.columnName === MeasuringEquipmentsControlName.DateUsed,
        );
        this.descriptionControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((value) => {
            if (descriptionColumn) {
                this.store.dispatch(
                    updateMeasurementEquipmentColumnData({
                        columnId: descriptionColumn.columnId,
                        rowId: this.row.rowId,
                        value: value || '',
                    }),
                );
            }
        });

        this.equipmentIdControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((value) => {
            if (equipmentIdColumn) {
                this.store.dispatch(
                    updateMeasurementEquipmentColumnData({
                        columnId: equipmentIdColumn.columnId,
                        rowId: this.row.rowId,
                        value: value || '',
                    }),
                );
            }
        });

        this.calibratedControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((value) => {
            if (calibratedColumn) {
                const newDate = moment(value);
                const calibratedDateValue =
                    value === FormsConstants.NA
                        ? FormsConstants.NA
                        : newDate.isValid()
                          ? newDate.format(ConfigConstants.formatPatternDateCosima)
                          : '';
                this.store.dispatch(
                    updateMeasurementEquipmentColumnData({
                        columnId: calibratedColumn.columnId,
                        rowId: this.row.rowId,
                        value: calibratedDateValue,
                    }),
                );
            }
        });

        this.dateUsedControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((value) => {
            if (dateUsedColumn) {
                const newDate = moment(value);
                const dateValue =
                    value === FormsConstants.NA
                        ? FormsConstants.NA
                        : newDate.isValid()
                          ? newDate.format(ConfigConstants.formatPatternDateCosima)
                          : '';
                this.store.dispatch(
                    updateMeasurementEquipmentColumnData({
                        columnId: dateUsedColumn.columnId,
                        rowId: this.row.rowId,
                        value: dateValue,
                    }),
                );
            }
        });
        const calibratedValue =
            calibratedColumn?.value === '' ? '' : moment(calibratedColumn?.value).format(HTML5_FMT.DATE);

        const dateUsedValue = dateUsedColumn?.value === '' ? '' : moment(dateUsedColumn?.value).format(HTML5_FMT.DATE);

        this.descriptionControl.setValue(descriptionColumn?.value, { emitEvent: false });
        this.calibratedControl.setValue(calibratedValue, { emitEvent: false });
        this.equipmentIdControl.setValue(equipmentIdColumn?.value, { emitEvent: false });
        this.dateUsedControl.setValue(dateUsedValue, { emitEvent: false });
    }

    private calibratedDateValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const usedDate =
                this.row?.columns.length > 3 ? moment(this.dateUsedControl?.value) : moment().startOf('day');
            const calibratedDate = moment(control.value);

            if (calibratedDate.isBefore(usedDate)) {
                return { invalidDate: true };
            }

            return null;
        };
    }

    private dateUsedValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const dateUsed = moment(control.value);
            const calibratedDate = moment(this.calibratedControl?.value);

            if (dateUsed.isAfter(calibratedDate)) {
                this.calibratedControl?.setErrors({ invalidDate: true });
            } else {
                this.calibratedControl?.setErrors(null);
            }

            return null;
        };
    }

    private updateMeasuringEquipmentsValue(noToolsUsed: boolean): void {
        this.measuringEquipmentForm.reset();
        if (noToolsUsed && this.row.visibility) {
            this.descriptionControl.patchValue(FormsConstants.NA);
            this.equipmentIdControl.patchValue(FormsConstants.NA);
            this.calibratedControl.patchValue(FormsConstants.NA);
            this.dateUsedControl.patchValue(FormsConstants.NA);
        }
    }
}
